import type { ReactFlowInstance } from 'reactflow';
import type { CustomNode, CustomEdge } from '@src/components/custom-nodes/types';
import {
  createDirectedEdge,
  getNearestNodeByType,
  getNodesBetween,
  recalculateHandlePositions
} from '@src/scenario-editor/render-utils'
import { recalculateNodesYPosForNodes, recalculateNodesXPos } from '@src/scenario-editor/utils';

interface DeleteBranchProps {
  reactFlowInstance?: ReactFlowInstance;
}
export const deleteStatementChildren = ({ reactFlowInstance }: DeleteBranchProps ) => (id: string) => {
  const nodes = (reactFlowInstance?.getNodes() ?? []) as CustomNode[];
  const edges = (reactFlowInstance?.getEdges() ?? []) as CustomEdge[];
  const setNodes = reactFlowInstance?.setNodes;
  const setEdges = reactFlowInstance?.setEdges;
  const nodeWithCurrentId = nodes.find((node) => node.id === id)!;
  const nearestIfCloseNode = getNearestNodeByType(nodes, edges, nodeWithCurrentId, 'IfCloseNode')!;
  const nodesInsideIfStatement = getNodesBetween(nodes, edges, nodeWithCurrentId, nearestIfCloseNode);
  const edgesWithSourceFromChildBranch =
    edges
      .filter((edge) => {
        const relevantNodes = [nodeWithCurrentId, ...nodesInsideIfStatement].map((node) => node.id);
        return relevantNodes.includes(edge.source) || relevantNodes.includes(edge.target);
      });
  const nodeIdBeforeBranch = edgesWithSourceFromChildBranch.find((edge) => edge.target === id)!.source;

  const edgeAfterFinalTargetNodeInBranch =
    edges.find((edge) => edge.source === nearestIfCloseNode.id);
  const nodeIdAfterFinalTargetNodeInBranch = edgeAfterFinalTargetNodeInBranch?.target;
  const nodeAfterFinalTargetNodeInBranch =
    nodes.find((node) => node.id === nodeIdAfterFinalTargetNodeInBranch);

  setNodes?.((nodes) => {
    const result = nodes
      .filter((node) => !([nodeWithCurrentId, ...nodesInsideIfStatement, nearestIfCloseNode])
      .map((node) => node.id).includes(node.id)) as CustomNode[];

    recalculateNodesXPos(result, undefined, { instance: reactFlowInstance });

    return result;
  });

  setEdges?.((edges) => {
    const filteredEdgesFromPreviousConnections =
      edges
        .filter((edge) => !([...edgesWithSourceFromChildBranch, edgeAfterFinalTargetNodeInBranch])
        .map((edge) => edge!.id).includes(edge.id));

    return filteredEdgesFromPreviousConnections
      .concat(createDirectedEdge(nodeIdBeforeBranch, nodeAfterFinalTargetNodeInBranch?.id ?? ''));
  });

  setTimeout(() => {
    setNodes?.(() => {
      const nodes = reactFlowInstance?.getNodes() as CustomNode[];
      const edges = reactFlowInstance?.getEdges() as CustomEdge[];
      recalculateNodesYPosForNodes(nodes, edges);
      return nodes;
    });
    setEdges?.(() => {
      const nodes = reactFlowInstance?.getNodes() as CustomNode[];
      const edges = reactFlowInstance?.getEdges() as CustomEdge[];
      recalculateHandlePositions(nodes, edges);
      return edges;
    });
  }, 0);

  return {
    success: true,
    message: 'Branch deleted.'
  };
}
